/**
 * Dynamic Field Utility
 * ─────────────────────────────────────────────────────────────
 * Centralised type definitions and resolvers for the SAP
 * reimbursement dynamic field configuration.
 *
 * ViewOfFieldType values (from the API's fieldTypes object):
 *   ""  → Date Type (blank = date field)
 *   "C" → Checkbox
 *   "D" → Dropdown (Values from Domain)
 *   "E" → Dropdown (Values from Customizing)
 *   "F" → F4 Help (Values from Domain)
 *   "G" → F4 Help (Values from Customizing)
 *   "T" → Text Box (Free Text)
 *   "S" → Summation Field (numeric read-only total)
 *   "L" → Line-Number (auto-generated, display only)
 */

// ── Types ────────────────────────────────────────────────────────────────────

export type FieldType = "" | "C" | "D" | "E" | "F" | "G" | "T" | "S" | "L" | "B" | "A" | "VD";

export interface FieldOption {
    SelectedValue: string;
    SelectedText: string;
    [key: string]: any;
}

export interface DynamicFieldConfig {
    /** SAP key used in ToDynamics / ToMultiLines */
    HeaderCustomerField: string;
    LineCustomerField?: string;
    Label: string;
    ViewOfFieldType: FieldType;
    OptionsValue: { results: FieldOption[] };
    DefaultValue: string;
    CustFldMandatory: boolean | string;
    VisibleToEmployee: boolean | string;
    FieldVisibleToApp: boolean | string;
    EnabledForEmployee: boolean | string;
    ReadOnlyForEmp: boolean | string;
    ReadOnlyForApp: boolean | string;
    FieldsOrder: string;
    UIPosition?: string;
    InformationText?: string;
    FunctionModule?: string;
    [key: string]: any;
}

// ── Type guards ──────────────────────────────────────────────────────────────

export const isBoolean = (v: boolean | string | undefined): boolean =>
    v === true || String(v || "").toLowerCase() === "true";

// ── Field-type helpers ───────────────────────────────────────────────────────

/** Returns true when a field should render as any variant of dropdown */
export const isDropdownType = (type: FieldType): boolean =>
    ["D", "E", "F", "G"].includes(type);

/**
 * Returns true when a field is a time field.
 * In SAP conventions, CTM prefix or fields/labels containing 'time' represent Time fields.
 */
export const isTimeType = (type: FieldType, fieldName: string = "", label: string = ""): boolean => {
    const nameUpper = String(fieldName || "").toUpperCase();
    const labelLower = String(label || "").toLowerCase();
    return (
        nameUpper.startsWith("CTM") ||
        nameUpper.includes("TIME") ||
        labelLower.includes("time")
    );
};

/**
 * Returns true when a field is a date field.
 * The SAP convention is that an empty string for ViewOfFieldType means Date.
 * Field names starting with "CDT" or containing "DT" in a specific pattern
 * are also date fields. Time fields are explicitly excluded.
 */
export const isDateType = (type: FieldType, fieldName: string = "", label: string = ""): boolean => {
    if (isTimeType(type, fieldName, label)) return false;
    if (type === "") return true;
    // Secondary check: CDT prefix field names (SAP convention)
    if (fieldName.startsWith("CDT")) return true;
    const nameLower = String(fieldName || "").toLowerCase();
    const labelLower = String(label || "").toLowerCase();
    if (nameLower.includes("date") || labelLower.includes("date")) return true;
    return false;
};

export const isCheckboxType = (type: FieldType, fieldName: string = ""): boolean => {
    if (type === "C") return true;
    const nameUpper = String(fieldName || "").toUpperCase();
    return nameUpper.startsWith("CKB") || nameUpper === "CLMCF" || nameUpper === "SLFTR";
};

export const isTextType = (type: FieldType): boolean =>
    type === "T" || type === "S" || type === "L" || type === "B";

// ── Profile and Transaction Field Classifiers ───────────────────────────────

/**
 * Returns true if the field is a designated employee profile field.
 * These are strictly personal to the employee profile (Pay Scale, Basic Salary, Employment Status, Phone)
 * and must be sourced from user master data rather than user-entered receipts.
 */
export const isProfileField = (
    fieldOrLabel: string | { Label?: string; HeaderCustomerField?: string }
): boolean => {
    const label = typeof fieldOrLabel === "string" ? fieldOrLabel : (fieldOrLabel?.Label ?? "");
    const l = label.toLowerCase().trim();
    return (
        l.includes("pay scale") ||
        l.includes("basic salary") ||
        l.includes("recruitment") ||
        l.includes("employment status") ||
        l.includes("employment type") ||
        l === "mobile number" ||
        l === "contact no"
    );
};

/**
 * Checks whether a default value is an invalid copy-paste artifact from SAP configuration
 * (e.g. Pay Scale string "50000-160000 AM" or user salary leaking into a non-payscale/non-salary field).
 */
export const isInvalidDefaultValue = (
    fieldOrLabel: string | { Label?: string; HeaderCustomerField?: string },
    defaultValue?: any,
    userPayScale?: string,
    userSalary?: string
): boolean => {
    if (defaultValue === undefined || defaultValue === null || defaultValue === "") return false;
    const label = typeof fieldOrLabel === "string" ? fieldOrLabel : (fieldOrLabel?.Label ?? "");
    const l = label.toLowerCase().trim();
    const strVal = String(defaultValue).trim();

    // If it's the actual Pay Scale field, a pay scale value is legitimate
    if (l.includes("pay scale")) return false;

    // Pattern matching Indian PSU / DMRC pay scale format: e.g. "50000-160000 AM", "60000-180000", "50000-160000"
    const isPayScalePattern =
        /^\d{4,6}\s*-\s*\d{4,6}/.test(strVal) ||
        (strVal.includes("-") && /\b(AM|AM\(E|AM\(NE|MGR|DGM|JGM|AGM|GM|ED|DIRECTOR|EXECUTIVE|NON-EXECUTIVE)\b/i.test(strVal));
    if (isPayScalePattern) return true;

    // Check if matches user's specific pay scale
    if (userPayScale && userPayScale.trim() && strVal === userPayScale.trim()) return true;

    // If it's NOT basic salary or an amount field, check if it matches basic salary
    const isAmountField =
        l.includes("salary") ||
        l.includes("amount") ||
        l.includes("cost") ||
        l.includes("fee") ||
        l.includes("rate");
    if (!isAmountField && userSalary && userSalary.trim() && strVal === userSalary.trim()) {
        return true;
    }

    return false;
};

/**
 * Checks whether a dynamic field is a transaction user-entry field that must start empty.
 * In SAP, transaction fields are editable by the employee (ReadOnlyForEmp is false)
 * and are not designated employee profile fields.
 */
export const isEditableUserEntryField = (
    field: DynamicFieldConfig | { Label?: string; HeaderCustomerField?: string; ReadOnlyForEmp?: any }
): boolean => {
    if (isProfileField(field)) return false;
    const readOnly = isBoolean(field.ReadOnlyForEmp);
    return !readOnly;
};

// ── SAP Date parsers ─────────────────────────────────────────────────────────

/** Parse /Date(ms)/ → "DD-MM-YYYY" */
export const parseMsDate = (raw: any): string => {
    if (!raw) return "";
    const s = String(raw).trim();
    const match = s.match(/\/Date\((-?\d+)\)\//);
    if (match) {
        const d = new Date(parseInt(match[1], 10));
        if (isNaN(d.getTime())) return "";
        const dd = String(d.getUTCDate()).padStart(2, "0");
        const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
        return `${dd}-${mm}-${d.getUTCFullYear()}`;
    }
    // ISO or date-time with T
    if (s.includes("T")) {
        const [date] = s.split("T");
        const [y, m, d2] = date.split("-");
        return y && m && d2 ? `${d2}-${m}-${y}` : s;
    }
    // YYYY-MM-DD → DD-MM-YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [y, m, d2] = s.split("-");
        return `${d2}-${m}-${y}`;
    }
    // YYYYMMDD
    if (/^\d{8}$/.test(s)) {
        return `${s.slice(6, 8)}-${s.slice(4, 6)}-${s.slice(0, 4)}`;
    }
    return s;
};

export const parseToIso = (raw: any): string => {
    if (!raw) return "";
    const s = String(raw).trim();
    const match = s.match(/\/Date\((-?\d+)\)\//);
    if (match) {
        const d = new Date(parseInt(match[1], 10));
        if (isNaN(d.getTime())) return "";
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, "0");
        const day = String(d.getUTCDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    }
    if (s.includes("T")) {
        const [date] = s.split("T");
        return date;
    }
    if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
    // DD-MM-YYYY → YYYY-MM-DD
    if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
        const [d2, m2, y2] = s.split("-");
        return `${y2}-${m2}-${d2}`;
    }
    // DD.MM.YYYY → YYYY-MM-DD
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(s)) {
        const [d2, m2, y2] = s.split(".");
        return `${y2}-${m2}-${d2}`;
    }
    // DD/MM/YYYY → YYYY-MM-DD
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
        const [d2, m2, y2] = s.split("/");
        return `${y2}-${m2}-${d2}`;
    }
    return s;
};

// ── Value resolver for VIEW (read-only display) ───────────────────────────────

/**
 * Resolves the display value for a field in VIEW mode.
 *
 * Priority:
 *  1. Value from ToDynamics / row object (rawValue)
 *  2. DefaultValue for SAP-pre-filled read-only fields
 *
 * Rendering logic by ViewOfFieldType:
 *  "" (Date)  → parseMsDate or format stored ISO string
 *  "C"        → special (caller handles with checkbox icon)
 *  "E","G","D","F" (Dropdown) → look up SelectedText from OptionsValue
 *  "T","S","L","B" (Text)    → raw string, fallback DefaultValue
 */
export const resolveDisplayValue = (
    field: DynamicFieldConfig,
    rawValue: any,
    userPayScale?: string,
    userSalary?: string
): string => {
    const type = field.ViewOfFieldType as FieldType;
    const opts: FieldOption[] = field.OptionsValue?.results ?? [];

    let value = rawValue;

    // Check if rawValue itself is an invalid leaked default (e.g. Pay Scale string leaking into TIN No.)
    if (isInvalidDefaultValue(field, value, userPayScale, userSalary)) {
        value = "";
    }

    // Resolve: raw value first.
    // For empty values: ONLY genuinely read-only system fields (Cost Centre, Last Purchase Date)
    // may use DefaultValue, provided it is not an invalid leaked Pay Scale or Salary.
    // Editable user transaction fields (TIN, Bill No, Seller Name, Cost) MUST remain empty ("-").
    if (value === undefined || value === null || value === "") {
        const isReadOnly = isBoolean(field.ReadOnlyForEmp);
        const isEditable = isEditableUserEntryField(field);

        if (isReadOnly && !isEditable) {
            const rawDef = field.DefaultValue ?? "";
            if (!isInvalidDefaultValue(field, rawDef, userPayScale, userSalary)) {
                value = rawDef;
            } else {
                value = "";
            }
        } else {
            value = "";
        }
    }

    if (value === "" || value === null || value === undefined) return "-";

    // Time field
    if (isTimeType(type, field.HeaderCustomerField, field.Label)) {
        return String(value);
    }

    // Date field
    if (type === "" || isDateType(type, field.HeaderCustomerField, field.Label)) return parseMsDate(value) || String(value);

    // Checkbox
    if (type === "C") {
        const v = String(value).toUpperCase();
        return v === "YES" || v === "TRUE" || v === "X" ? "Yes" : "No";
    }

    // Dropdown types – look up the SelectedText label
    if (isDropdownType(type) && opts.length > 0) {
        const match = opts.find((o) => String(o.SelectedValue) === String(value));
        if (match?.SelectedText) return match.SelectedText;
    }

    // Text / summation / line-no
    return String(value);
};

// ── Filter & sort fields for display ─────────────────────────────────────────

/**
 * Returns only the fields that should be visible to the employee, sorted by FieldsOrder.
 * Pass `mode = "view"` for read-only display, `mode = "create"` for form input.
 */
export const getVisibleFields = (
    fields: DynamicFieldConfig[],
    mode: "view" | "create" = "view"
): DynamicFieldConfig[] => {
    if (!Array.isArray(fields)) return [];

    return fields
        .filter((f) => {
            // Only show fields explicitly marked visible to employee (same as mobile)
            return isBoolean(f.VisibleToEmployee);
        })
        .sort((a, b) => {
            const oa = parseInt(a.FieldsOrder ?? "0", 10);
            const ob = parseInt(b.FieldsOrder ?? "0", 10);
            if (oa !== ob) return oa - ob;
            // Secondary sort by UIPosition (L=0, C=1, R=2)
            const posOrder: Record<string, number> = { L: 0, C: 1, R: 2 };
            const pa = posOrder[(a.UIPosition ?? "L")[0]] ?? 0;
            const pb = posOrder[(b.UIPosition ?? "L")[0]] ?? 0;
            return pa - pb;
        });
};

/**
 * Given the full DynamicFields array and a ToDynamics object,
 * returns an enriched array with the resolved display value added.
 */
export const enrichFieldsWithValues = (
    fields: DynamicFieldConfig[],
    toDynamics: Record<string, any>,
    userPayScale?: string,
    userSalary?: string
): Array<DynamicFieldConfig & { resolvedValue: string; rawValue: any }> => {
    return fields.map((f) => {
        const key = f.HeaderCustomerField;
        const rawValue = key ? toDynamics[key] : undefined;
        const resolvedValue = resolveDisplayValue(f, rawValue, userPayScale, userSalary);
        return { ...f, rawValue, resolvedValue };
    });
};

// ── Line items helper ─────────────────────────────────────────────────────────

/** Resolve a line-item cell value using MultilineTableHeaders config */
export const resolveLineValue = (col: DynamicFieldConfig, lineRow: Record<string, any>): string => {
    const key = col.LineCustomerField ?? "";
    const rawVal = lineRow[key];
    const type = col.ViewOfFieldType as FieldType;
    const opts: FieldOption[] = col.OptionsValue?.results ?? [];

    if (rawVal === undefined || rawVal === null || rawVal === "") return "-";

    // Special composite family member field
    if (key === "FAOBJ") {
        const rel = lineRow["FAMTX"] ?? "";
        const name = lineRow["FCNAM"] ?? "";
        return rel || name ? `${rel}: ${name}` : "-";
    }

    // Time
    if (isTimeType(type, key, col.Label)) return String(rawVal);

    // Date
    if (type === "" || isDateType(type, key, col.Label) || key === "CDT01" || key.startsWith("CDT")) return parseMsDate(rawVal) || String(rawVal);

    // Boolean
    if (rawVal === true || rawVal === false) return rawVal ? "Yes" : "No";

    // Dropdown
    if (isDropdownType(type) && opts.length > 0) {
        const match = opts.find((o) => String(o.SelectedValue) === String(rawVal));
        if (match?.SelectedText) return match.SelectedText;
    }

    return String(rawVal);
};
